import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useToast } from './ToastContext';
import { useTranslation } from 'react-i18next';
import { addWishlistItem, getWishlist, removeWishlistItem } from '../api/wishlistApi';

const CartContext = createContext();
const WISHLIST_STORAGE_KEY = 'wishlist';
const WISHLIST_TTL_MS = 30 * 60 * 1000;
const WISHLIST_MERGE_FLAG_KEY = 'wishlistMergedForToken';
const CART_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

const readWishlistFromStorage = () => {
    try {
        const savedWishlist = localStorage.getItem(WISHLIST_STORAGE_KEY);
        if (!savedWishlist) return [];

        const parsed = JSON.parse(savedWishlist);

        // Backward compatibility: previously wishlist was stored as plain array
        if (Array.isArray(parsed)) return parsed;

        if (!parsed || !Array.isArray(parsed.items)) {
            localStorage.removeItem(WISHLIST_STORAGE_KEY);
            return [];
        }

        if (!parsed.expiresAt || Date.now() > parsed.expiresAt) {
            localStorage.removeItem(WISHLIST_STORAGE_KEY);
            return [];
        }

        return parsed.items;
    } catch (error) {
        localStorage.removeItem(WISHLIST_STORAGE_KEY);
        return [];
    }
};

const writeWishlistToStorage = (items) => {
    localStorage.setItem(
        WISHLIST_STORAGE_KEY,
        JSON.stringify({
            items,
            expiresAt: Date.now() + WISHLIST_TTL_MS
        })
    );
};

const getPrimaryIdentifier = (item) => item?._id || item?.id;

const identifiersMatch = (item, targetId) => {
    if (!item || targetId === undefined || targetId === null) return false;
    const idCandidates = [item._id, item.id].filter(Boolean).map(String);
    return idCandidates.includes(String(targetId));
};

const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const readCartFromStorage = () => {
    try {
        const savedCart = localStorage.getItem('cart');
        const cartExpiry = localStorage.getItem('cartExpiry');
        if (!savedCart || !cartExpiry) {
            return [];
        }
        const expiryMs = parseInt(cartExpiry, 10);
        if (Number.isNaN(expiryMs) || Date.now() >= expiryMs) {
            localStorage.removeItem('cart');
            localStorage.removeItem('cartExpiry');
            return [];
        }
        const parsed = JSON.parse(savedCart);
        if (!Array.isArray(parsed)) {
            localStorage.removeItem('cart');
            localStorage.removeItem('cartExpiry');
            return [];
        }
        return parsed;
    } catch {
        localStorage.removeItem('cart');
        localStorage.removeItem('cartExpiry');
        return [];
    }
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const { addToast } = useToast();
    const { t } = useTranslation();

    const isObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || ''));

    const normalizeWishlistItems = (wishlist = []) => {
        return wishlist
            .map((entry) => {
                const menuItem = entry?.menuItem || entry;
                if (!menuItem) return null;

                const normalizedImage = Array.isArray(menuItem.imageUrl)
                    ? menuItem.imageUrl[0]
                    : menuItem.imageUrl;

                return {
                    ...menuItem,
                    imageUrl: normalizedImage,
                    _wishlistAddedAt: entry?.addedAt || menuItem?._wishlistAddedAt
                };
            })
            .filter(Boolean);
    };

    const [authToken, setAuthToken] = useState(() => localStorage.getItem('token') || '');

    const [cartItems, setCartItems] = useState(() => readCartFromStorage());

    const [wishlistItems, setWishlistItems] = useState(() => {
        return readWishlistFromStorage();
    });

    const [cartRemainingTime, setCartRemainingTime] = useState(0);

    const cartItemsRef = useRef(cartItems);
    cartItemsRef.current = cartItems;

    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cartItems));
    }, [cartItems]);

    useEffect(() => {
        if (cartItems.length > 0) {
            localStorage.setItem('cartExpiry', Date.now() + CART_EXPIRY_MS);
        } else {
            localStorage.removeItem('cartExpiry');
        }
    }, [cartItems]);

    useEffect(() => {
        writeWishlistToStorage(wishlistItems);
    }, [wishlistItems]);

    useEffect(() => {
        const syncTokenState = () => {
            const latestToken = localStorage.getItem('token') || '';
            setAuthToken((prev) => (prev === latestToken ? prev : latestToken));
        };

        window.addEventListener('focus', syncTokenState);
        window.addEventListener('storage', syncTokenState);

        return () => {
            window.removeEventListener('focus', syncTokenState);
            window.removeEventListener('storage', syncTokenState);
        };
    }, []);

    useEffect(() => {
        let isCancelled = false;

        const syncWishlist = async () => {
            if (!authToken) {
                setWishlistItems(readWishlistFromStorage());
                return;
            }

            try {
                let response = await getWishlist();
                let serverWishlist = normalizeWishlistItems(response?.data?.wishlist || []);

                const alreadyMergedToken = sessionStorage.getItem(WISHLIST_MERGE_FLAG_KEY);
                if (alreadyMergedToken !== authToken) {
                    const localWishlist = readWishlistFromStorage();
                    const serverIds = new Set(
                        serverWishlist
                            .map((item) => item?._id)
                            .filter(Boolean)
                            .map(String)
                    );

                    const mergeCandidates = localWishlist.filter(
                        (item) => item?._id && isObjectId(item._id) && !serverIds.has(String(item._id))
                    );

                    if (mergeCandidates.length > 0) {
                        await Promise.allSettled(
                            mergeCandidates.map((item) => addWishlistItem(item._id))
                        );
                        response = await getWishlist();
                        serverWishlist = normalizeWishlistItems(response?.data?.wishlist || []);
                    }

                    sessionStorage.setItem(WISHLIST_MERGE_FLAG_KEY, authToken);
                }

                if (!isCancelled) {
                    setWishlistItems(serverWishlist);
                }
            } catch (error) {
                if (error?.status === 401 && !isCancelled) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('userId');
                    setAuthToken('');
                    setWishlistItems(readWishlistFromStorage());
                    return;
                }

                console.error('Failed to sync wishlist from server:', error);
                if (!isCancelled) {
                    setWishlistItems(readWishlistFromStorage());
                }
            }
        };

        syncWishlist();

        return () => {
            isCancelled = true;
        };
    }, [authToken]);

    useEffect(() => {
        const interval = setInterval(() => {
            const expiry = localStorage.getItem('cartExpiry');
            if (expiry) {
                const remaining = Math.max(0, parseInt(expiry, 10) - Date.now());
                setCartRemainingTime(remaining);
                if (remaining === 0) {
                    setCartItems([]);
                    localStorage.removeItem('cart');
                    localStorage.removeItem('cartExpiry');
                }
            } else {
                setCartRemainingTime(0);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const addToCart = (item) => {
        const itemId = getPrimaryIdentifier(item);
        const prevCart = cartItemsRef.current;
        const existingItem = prevCart.find((i) => identifiersMatch(i, itemId));
        if (existingItem) {
            addToast(t('toast.itemUpdated'), 'success');
        } else {
            addToast(t('toast.addedToCart'), 'success');
        }

        setCartItems((prevItems) => {
            const existing = prevItems.find((i) => identifiersMatch(i, itemId));
            if (existing) {
                return prevItems.map((i) =>
                    identifiersMatch(i, itemId) ? { ...i, quantity: i.quantity + 1 } : i
                );
            }
            return [...prevItems, { ...item, quantity: 1 }];
        });
    };

    const removeFromCart = (itemId) => {
        setCartItems(prevItems => prevItems.filter(item => !identifiersMatch(item, itemId)));
    };

    const updateCartItemQuantity = (itemId, quantity) => {
        if (quantity < 1) return;
        setCartItems(prevItems =>
            prevItems.map(item =>
                identifiersMatch(item, itemId) ? { ...item, quantity } : item
            )
        );
    };

    const clearCart = () => {
        setCartItems([]);
    };

    const addToWishlist = async (item) => {
        const itemId = getPrimaryIdentifier(item);

        if (wishlistItems.some((existingItem) => identifiersMatch(existingItem, itemId))) {
            addToast(t('toast.alreadyInWishlist'), 'info');
            return;
        }

        if (authToken && item?._id) {
            try {
                const response = await addWishlistItem(item._id);
                setWishlistItems(normalizeWishlistItems(response?.data?.wishlist || []));
                addToast(t('toast.addedToWishlist'), 'success');
                return;
            } catch (error) {
                addToast(error?.message || t('menu.error', 'Failed to update wishlist'), 'error');
                return;
            }
        }

        addToast(t('toast.addedToWishlist'), 'success');
        const normalizedImage = Array.isArray(item.imageUrl) ? item.imageUrl[0] : item.imageUrl;
        setWishlistItems((prevItems) => {
            if (prevItems.some((existingItem) => identifiersMatch(existingItem, itemId))) {
                return prevItems;
            }
            return [...prevItems, { ...item, imageUrl: normalizedImage }];
        });
    };

    const removeFromWishlist = async (itemId) => {
        if (authToken) {
            const currentItem = wishlistItems.find((item) => identifiersMatch(item, itemId));
            const backendId = currentItem?._id || (isObjectId(itemId) ? itemId : null);

            if (!backendId) {
                addToast(t('toast.removedFromWishlist'), 'success');
                setWishlistItems((prevItems) => prevItems.filter((item) => !identifiersMatch(item, itemId)));
                return;
            }

            try {
                const response = await removeWishlistItem(backendId);
                setWishlistItems(normalizeWishlistItems(response?.data?.wishlist || []));
                addToast(t('toast.removedFromWishlist'), 'success');
                return;
            } catch (error) {
                addToast(error?.message || t('menu.error', 'Failed to update wishlist'), 'error');
                return;
            }
        }

        addToast(t('toast.removedFromWishlist'), 'success');
        setWishlistItems((prevItems) => prevItems.filter((item) => !identifiersMatch(item, itemId)));
    };

    const isInWishlist = (itemId) => {
        return wishlistItems.some((item) => identifiersMatch(item, itemId));
    };

    const moveToCart = async (itemId) => {
        const item = wishlistItems.find((existingItem) => identifiersMatch(existingItem, itemId));
        if (item) {
            addToCart(item);
            await removeFromWishlist(itemId);
        }
    };

    const getCartTotal = () => {
        return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    const getCartItemsCount = () => {
        return cartItems.reduce((count, item) => count + item.quantity, 0);
    };

    const value = {
        cartItems,
        wishlistItems,
        cartRemainingTime,
        formatTime,
        addToCart,
        removeFromCart,
        updateCartItemQuantity,
        clearCart,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        moveToCart,
        getCartTotal,
        getCartItemsCount
    };

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
